import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import defaultCover from '../assets/login-cover.png';

const LoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Branding State (Ready for dynamic injection)
  const [brand, setBrand] = useState({
    name: 'HoraMarcada Pro',
    logo: null as string | null,
    coverImage: defaultCover,
    primaryColor: 'var(--color-primary)' // Fallback to CSS var
  });

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const adminEmail = import.meta.env.VITE_ADMIN_EMAIL;
      if (adminEmail && user.email?.toLowerCase() === adminEmail.toLowerCase()) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { data, error: loginError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (loginError) throw loginError;

      if (data.session) {
        const adminEmail = import.meta.env.VITE_ADMIN_EMAIL;
        if (adminEmail && email.toLowerCase() === adminEmail.toLowerCase()) {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err: any) {
      console.error("Login error:", err);
      setError("Email ou senha inválidos.");
    } finally {
      setLoading(false);
    }
  };

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      setResetSent(true);
    } catch (err: any) {
      console.error("Reset password error:", err);
      setError("Erro ao enviar email de recuperação. Verifique o email informado.");
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="flex min-h-screen w-full bg-white dark:bg-black">
        <div className="hidden lg:flex w-1/2 relative bg-slate-900 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center transition-all duration-1000"
            style={{ backgroundImage: `url(${brand.coverImage})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30"></div>
          </div>
          <div className="relative z-10 flex flex-col justify-between w-full p-12 text-white">
            <div className="flex items-center gap-2">
              <div className="bg-white/10 backdrop-blur-md p-2 rounded-lg border border-white/20">
                <span className="material-symbols-outlined text-2xl">calendar_today</span>
              </div>
              <span className="text-lg font-bold tracking-wide uppercase opacity-90">{brand.name}</span>
            </div>
            <div className="space-y-6 max-w-lg">
              <h1 className="text-4xl font-bold leading-tight">
                Recuperação de <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-primary">Acesso.</span>
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed">
                Não se preocupe, vamos te ajudar a recuperar o acesso à sua conta em poucos passos.
              </p>
            </div>
            <div className="flex gap-4 text-sm font-medium text-slate-400">
              <span>© 2026 HoraMarcada Pro</span>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background-light dark:bg-background-dark">
          <div className="w-full max-w-md space-y-8">
            <div className="text-center lg:text-left space-y-2">
              <button
                onClick={() => { setShowForgotPassword(false); setResetSent(false); setError(null); }}
                className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-sm font-medium transition-colors mb-4"
              >
                <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                Voltar para login
              </button>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Recuperar Senha</h2>
              <p className="text-slate-500 dark:text-slate-400">
                {resetSent ? 'Verifique seu email.' : 'Informe seu email para receber o link de redefinição.'}
              </p>
            </div>

            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-xl flex items-center gap-3 border border-red-100 dark:border-red-900/50">
                <span className="material-symbols-outlined text-xl">error</span>
                {error}
              </div>
            )}

            {resetSent ? (
              <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-2xl border border-green-100 dark:border-green-900/50 text-center animate-in fade-in zoom-in duration-300">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="material-symbols-outlined text-3xl text-green-600 dark:text-green-300">mail</span>
                </div>
                <h3 className="text-lg font-bold text-green-800 dark:text-green-200 mb-2">Email enviado!</h3>
                <p className="text-green-700 dark:text-green-300 text-sm mb-6">
                  Enviamos um link de recuperação para <strong>{resetEmail}</strong>. Verifique sua caixa de entrada e spam.
                </p>
                <button
                  onClick={() => { setShowForgotPassword(false); setResetSent(false); }}
                  className="bg-white dark:bg-black text-green-700 dark:text-green-300 font-bold py-2.5 px-6 rounded-lg text-sm border border-green-200 dark:border-green-800 shadow-sm hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
                >
                  Voltar ao Login
                </button>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleResetPassword}>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300" htmlFor="reset-email">E-mail</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 select-none">
                      <span className="material-symbols-outlined text-[20px]">mail</span>
                    </span>
                    <input
                      id="reset-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      className="w-full rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-black/20 text-slate-900 dark:text-white focus:border-primary focus:ring-4 focus:ring-primary/10 h-12 pl-11 pr-4 transition-all placeholder:text-slate-400"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary-dark text-white h-12 rounded-xl font-bold text-base shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed transform active:scale-[0.98]"
                >
                  {loading ? <span className="material-symbols-outlined animate-spin">progress_activity</span> : 'Enviar Link de Recuperação'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full bg-white dark:bg-black">
      {/* Left Side - Brand / Cover */}
      <div className="hidden lg:flex w-1/2 relative bg-slate-900 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center transition-all duration-1000"
          style={{ backgroundImage: `url(${brand.coverImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30"></div>
        </div>

        <div className="relative z-10 flex flex-col justify-between w-full p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="bg-white/10 backdrop-blur-md p-2 rounded-lg border border-white/20">
              <span className="material-symbols-outlined text-2xl">calendar_today</span>
            </div>
            <span className="text-lg font-bold tracking-wide uppercase opacity-90">{brand.name}</span>
          </div>

          <div className="space-y-6 max-w-lg">
            <h1 className="text-5xl font-bold leading-tight">
              Gestão de beleza <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-primary">inteligente.</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Organize sua agenda, fidelize clientes e controle seu financeiro em um só lugar.
              A plataforma completa para salões e profissionais independentes.
            </p>
          </div>

          <div className="flex gap-4 text-sm font-medium text-slate-400">
            <span>© 2026 HoraMarcada Pro</span>
            <span>•</span>
            <a href="#" className="hover:text-white transition-colors">Termos</a>
            <a href="#" className="hover:text-white transition-colors">Privacidade</a>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background-light dark:bg-background-dark">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Header (Only visible on small screens) */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <span className="material-symbols-outlined text-primary">calendar_today</span>
              <span className="font-bold text-primary">{brand.name}</span>
            </div>
          </div>

          <div className="text-center lg:text-left space-y-2">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Bem-vindo de volta</h2>
            <p className="text-slate-500 dark:text-slate-400">
              Digite suas credenciais para acessar o painel.
            </p>
          </div>

          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-xl flex items-center gap-3 border border-red-100 dark:border-red-900/50">
              <span className="material-symbols-outlined text-xl">error</span>
              {error}
            </div>
          )}

          <form className="space-y-5" onSubmit={handleLogin}>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300" htmlFor="email">E-mail</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 select-none">
                  <span className="material-symbols-outlined text-[20px]">mail</span>
                </span>
                <input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-black/20 text-slate-900 dark:text-white focus:border-primary focus:ring-4 focus:ring-primary/10 h-12 pl-11 pr-4 transition-all placeholder:text-slate-400"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300" htmlFor="password">Senha</label>
                <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm font-semibold text-primary hover:text-primary-dark transition-colors">Esqueceu?</button>
              </div>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 select-none">
                  <span className="material-symbols-outlined text-[20px]">lock</span>
                </span>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Sua senha secreta"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-black/20 text-slate-900 dark:text-white focus:border-primary focus:ring-4 focus:ring-primary/10 h-12 pl-11 pr-12 transition-all placeholder:text-slate-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-0 h-full px-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                >
                  <span className="material-symbols-outlined text-[20px]">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary-dark text-white h-12 rounded-xl font-bold text-base shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed transform active:scale-[0.98]"
            >
              {loading ? <span className="material-symbols-outlined animate-spin">progress_activity</span> : (
                <>
                  <span>Acessar Painel</span>
                  <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
                </>
              )}
            </button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200 dark:border-slate-700"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background-light dark:bg-background-dark px-2 text-slate-500">ou</span>
            </div>
          </div>

          <div className="text-center">
            <span className="text-slate-500 dark:text-slate-400">Ainda não tem cadastro? </span>
            <button onClick={() => navigate('/signup')} className="text-primary hover:text-primary-dark font-bold hover:underline transition-all">
              Criar conta agora
            </button>
          </div>

          {/* Footer for Mobile/Desktop Right Panel */}
          <div className="pt-8 text-center lg:text-left">
            <button onClick={() => navigate('/')} className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-sm font-medium transition-colors">
              <span className="material-symbols-outlined text-[16px]">arrow_back</span>
              Voltar para o site
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;